import React from 'react';
import { XMarkIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface WrongAnswer {
  questionId: string;
  questionNumber: number; // Numero originale della domanda nel quiz
  question: {
    id: string;
    text: string;
    explanation?: string;
    answerOptions: Array<{
      id: string;
      text: string;
      isCorrect: boolean;
      order: number;
    }>;
  };
  selectedAnswer: {
    id: string;
    text: string;
    isCorrect: boolean;
    order: number;
  };
  correctAnswer: {
    id: string;
    text: string;
    isCorrect: boolean;
    order: number;
  };
  timeSpent: number;
}

interface WrongAnswersModalProps {
  isOpen: boolean;
  onClose: () => void;
  wrongAnswers: WrongAnswer[];
}

export const WrongAnswersModal: React.FC<WrongAnswersModalProps> = ({
  isOpen,
  onClose,
  wrongAnswers
}) => {
  if (!isOpen) return null;

  const formatTime = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-2 sm:p-4">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-sm sm:max-w-md w-full max-h-[75vh] sm:max-h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                Risposte Errate
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                {wrongAnswers.length} domande da rivedere
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-2 sm:p-3">
            <div className="space-y-2 sm:space-y-3">
              {wrongAnswers.map((answer, index) => (
                <div key={answer.questionId} className="card p-2 sm:p-3">
                  {/* Question Header */}
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs sm:text-sm font-semibold text-red-600">
                          {answer.questionNumber}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm sm:text-base font-medium text-gray-900 leading-relaxed">
                          {answer.question.text}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          Tempo impiegato: {formatTime(answer.timeSpent)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Answers */}
                  <div className="space-y-1 sm:space-y-2">
                    {/* Your Answer - Wrong */}
                    <div className="bg-red-50 border border-red-200 rounded-lg p-2 sm:p-3">
                      <div className="flex items-start space-x-2 sm:space-x-3">
                        <XCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs sm:text-sm font-medium text-red-800 mb-1">
                            La tua risposta
                          </p>
                          <p className="text-xs sm:text-sm text-red-700">
                            {answer.selectedAnswer.text}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Correct Answer */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3">
                      <div className="flex items-start space-x-2 sm:space-x-3">
                        <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs sm:text-sm font-medium text-green-800 mb-1">
                            Risposta corretta
                          </p>
                          <p className="text-xs sm:text-sm text-green-700">
                            {answer.correctAnswer.text}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Explanation if available */}
                    {answer.question.explanation && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3">
                        <div className="flex items-start space-x-2 sm:space-x-3">
                          <div className="w-4 h-4 sm:w-5 sm:h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-white text-xs">i</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-xs sm:text-sm font-medium text-blue-800 mb-1">
                              Spiegazione
                            </p>
                            <p className="text-xs sm:text-sm text-blue-700">
                              {answer.question.explanation}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-2 sm:p-3">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="btn-primary px-3 py-2 text-xs sm:text-sm"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
